// Neuer Kontakt / Kontakt bearbeiten als Glas-Sheet (Handoff Sonstige Kontakte §3, Screen 09).
// Felder, Optionen, Pflichtfelder und Texte wie KontaktErstellenFormular/KontaktEditFormular; Pflichtfelder inline.
import { useId, useState, type CSSProperties } from 'react';
import { AddressBook, Bank, Buildings, CaretUpDown, CircleNotch, FloppyDisk, MapPin, PencilSimple, Phone, User, type Icon } from '@phosphor-icons/react';
import { MeldungDialog } from '../../../../components/ui/MeldungDialog';
import { Sheet } from '../../../../components/ui/Sheet';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import type { KontaktTab } from '../../../../lib/kontakte/formular';
import { useKontaktFormular, type KontaktFeld } from '../../../../lib/kontakte/useKontaktFormular';
import type { SonstigerKontaktZeile } from '../../../../lib/kontakte/useSonstigerKontakt';

const ANREDEN = ['Herr', 'Frau', 'Eheleute', 'Divers'];
const KONTAKTARTEN = ['Mieter', 'Eigentümer', 'Dienstleister', 'Handwerker', 'Makler', 'Sonstige'];
const UNTERNEHMENSARTEN = ['Mieter', 'Dienstleister', 'Handwerker', 'Immobilienverwaltung', 'Makler', 'Eigentümer', 'Bank', 'Hausmeister', 'Bekannter', 'Sonstige'];
const LAENDER = ['Deutschland', 'Österreich', 'Schweiz'];

interface FeldDef {
  label: string;
  feld: KontaktFeld;
  /** Spalten von 6 (mobil immer volle Breite) */
  span: number;
  optionen?: string[];
  /** Text der leeren Option (Land: „Wähle eine Option“) */
  leer?: string;
  typ?: 'text' | 'email' | 'date';
}

interface Abschnitt { titel: string; icon: Icon; farbe: number; felder: FeldDef[]; schalter?: boolean }

function abschnitte(tab: KontaktTab, erweitert: boolean): Abschnitt[] {
  const kopf: Abschnitt = tab === 'privatperson'
    ? { titel: 'Kontakt', icon: User, farbe: 0, schalter: true, felder: [
        { label: 'Anrede', feld: 'anrede', span: 2, optionen: ANREDEN },
        { label: 'Vorname', feld: 'vorname', span: 2 },
        { label: 'Nachname *', feld: 'nachname', span: 2 },
        { label: 'Kontaktart', feld: 'kontaktart', span: 3, optionen: KONTAKTARTEN },
        { label: 'Geburtsdatum', feld: 'geburtsdatum', span: 3, typ: 'date' },
      ] }
    : { titel: 'Unternehmensinformationen', icon: Buildings, farbe: 1, schalter: true, felder: [
        { label: 'Unternehmensart', feld: 'unternehmensart', span: 3, optionen: UNTERNEHMENSARTEN },
        { label: 'Firmenname *', feld: 'firmenname', span: 3 },
      ] };
  if (!erweitert) return [kopf];
  return [
    kopf,
    { titel: 'Adresse', icon: MapPin, farbe: 4, felder: [
      { label: 'Strasse', feld: 'strasse', span: 4 },
      { label: 'Hausnummer', feld: 'hausnummer', span: 2 },
      { label: 'PLZ', feld: 'plz', span: 2 },
      { label: 'Stadt', feld: 'stadt', span: 4 },
      { label: 'Land', feld: 'land', span: 6, optionen: LAENDER, leer: 'Wähle eine Option' },
    ] },
    { titel: 'Kontaktmöglichkeiten', icon: Phone, farbe: 2, felder: [
      { label: 'E-Mail', feld: 'email', span: 6, typ: 'email' },
      { label: 'Festnetz', feld: 'festnetz', span: 3 },
      { label: 'Handynummer', feld: 'handynummer', span: 3 },
    ] },
    { titel: 'Bankdaten', icon: Bank, farbe: 3, felder: [
      { label: 'Kontoinhaber', feld: 'kontoinhaber', span: 3 },
      { label: 'Bank', feld: 'bank', span: 3 },
      { label: 'IBAN', feld: 'iban', span: 3 },
      { label: 'BIC', feld: 'bic', span: 3 },
      { label: 'Steuernummer', feld: 'steuernummer', span: 3 },
      { label: 'USt-IdNr.', feld: 'ustIdNr', span: 3 },
      { label: 'Gläubiger-ID', feld: 'glaeubigerId', span: 3 },
    ] },
  ];
}

export function KontaktFormularSheet({ kontakt, onSchliessen, onGespeichert }: {
  /** null = Neuer Kontakt */
  kontakt: SonstigerKontaktZeile | null;
  onSchliessen: () => void;
  onGespeichert: (id: string | null) => void;
}) {
  const basisId = useId();
  const { mobil } = useMobilModus();
  const { felder, setze, setzeTab, erweitert, setErweitert, aktiv, setAktiv, fehler, saving, speichern } = useKontaktFormular(kontakt);
  const [meldung, setMeldung] = useState<string | null>(null);
  const bearbeiten = kontakt !== null;

  const absenden = async () => {
    const r = await speichern();
    if (r.ok) onGespeichert(r.id);
    else if (r.meldung) setMeldung(r.meldung);
    else document.getElementById(`${basisId}-${felder.activeTab === 'privatperson' ? 'nachname' : 'firmenname'}`)?.focus();
  };

  const feldStil = (mitFehler: boolean): CSSProperties => ({
    width: '100%', height: 42, border: 'none', borderRadius: 13, background: 'var(--sk-field)', fontSize: 14, fontWeight: 600, color: 'var(--ink)', outline: 'none',
    boxShadow: mitFehler ? 'inset 0 0 0 2px var(--bad)' : 'inset 0 0 0 1px var(--line)', transition: 'box-shadow .2s',
  });

  return (
    <Sheet
      titel={bearbeiten ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}
      icon={bearbeiten ? PencilSimple : AddressBook}
      iconFarbe="linear-gradient(155deg, var(--sk-av-1-a), var(--sk-av-1-b))"
      schliessenTip="Schließen – Ohne Speichern"
      breite={760}
      onSchliessen={onSchliessen}
    >
      <div className="sk-flaeche flex flex-col" style={{ gap: 16 }}>
        <div role="tablist" aria-label="Kontaktart" className="flex self-start" style={{ gap: 3, padding: 3, borderRadius: 15, background: 'var(--fill2)' }}>
          {(['privatperson', 'unternehmen'] as const).map(t => {
            const an = felder.activeTab === t;
            return (
              <button key={t} type="button" role="tab" aria-selected={an} onClick={() => setzeTab(t)} className="cursor-pointer"
                style={{ height: mobil ? 44 : 32, padding: '0 16px', border: 'none', borderRadius: 12, fontSize: 13, fontWeight: 700, color: 'var(--ink)', background: an ? 'var(--bg)' : 'transparent', boxShadow: an ? '0 2px 8px -3px var(--shade)' : 'none' }}>
                {t === 'privatperson' ? 'Privatperson' : 'Unternehmen'}
              </button>
            );
          })}
        </div>

        {abschnitte(felder.activeTab, erweitert).map(a => {
          const I = a.icon;
          return (
            <fieldset key={a.titel} className="m-0 flex min-w-0 flex-col" style={{ border: 'none', gap: 10, padding: 14, borderRadius: 20, background: 'var(--fill)' }}>
              <legend className="contents">
                <span className="flex items-center" style={{ gap: 8 }}>
                  <I aria-hidden weight="fill" size={15} color={`var(--sk-av-${a.farbe}-b)`} />
                  <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--ink2)' }}>{a.titel}</span>
                </span>
              </legend>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${mobil ? 1 : 6}, minmax(0, 1fr))`, gap: 10 }}>
                {a.felder.map(f => {
                  const id = `${basisId}-${f.feld}`;
                  const err = fehler?.feld === f.feld ? fehler.text : null;
                  return (
                    <div key={f.feld} className="flex min-w-0 flex-col" style={{ gridColumn: mobil ? '1 / -1' : `span ${f.span}`, gap: 5 }}>
                      <label htmlFor={id} style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink2)', paddingLeft: 4 }}>{f.label}</label>
                      {f.optionen ? (
                        <div className="relative">
                          <select id={id} value={felder[f.feld]} onChange={e => setze(f.feld, e.target.value)} className="cursor-pointer appearance-none"
                            style={{ ...feldStil(!!err), padding: '0 34px 0 12px' }}>
                            <option value="">{f.leer ?? ''}</option>
                            {f.optionen.map(o => <option key={o}>{o}</option>)}
                          </select>
                          <CaretUpDown aria-hidden weight="bold" size={13} color="var(--ink3)" className="pointer-events-none absolute" style={{ right: 12, top: '50%', transform: 'translateY(-50%)' }} />
                        </div>
                      ) : (
                        <input id={id} type={f.typ ?? 'text'} value={felder[f.feld]} onChange={e => setze(f.feld, e.target.value)}
                          aria-invalid={err ? true : undefined} aria-describedby={err ? `${id}-fehler` : undefined}
                          className="sk-eingabe" style={{ ...feldStil(!!err), padding: '0 12px' }} />
                      )}
                      {err && <span id={`${id}-fehler`} role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--bad)', paddingLeft: 4 }}>{err}</span>}
                    </div>
                  );
                })}
              </div>
              {a.schalter && (
                <button type="button" role="switch" aria-checked={aktiv} onClick={() => setAktiv(!aktiv)}
                  data-tip="Kontakt ist aktiv – Inaktive Kontakte erscheinen in der Liste nur mit „Auch Inaktive“"
                  className="flex cursor-pointer items-center text-left"
                  style={{ gap: 10, padding: '4px 2px', minHeight: mobil ? 44 : undefined, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>
                  <span aria-hidden className="relative shrink-0" style={{ width: 40, height: 24, borderRadius: 12, background: aktiv ? 'var(--ok)' : 'var(--fill3)', transition: 'background .25s' }}>
                    <span className="sk-knauf absolute" style={{ top: 2, left: aktiv ? 18 : 2, width: 20, height: 20, borderRadius: '50%', background: 'var(--sk-av-ink)', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' }} />
                  </span>
                  Kontakt ist aktiv
                </button>
              )}
              {a.schalter && (
                <button type="button" role="switch" aria-checked={erweitert} onClick={() => setErweitert(!erweitert)}
                  className="flex cursor-pointer items-center text-left"
                  style={{ gap: 10, padding: '4px 2px', minHeight: mobil ? 44 : undefined, border: 'none', background: 'transparent', fontSize: 13, fontWeight: 600, color: 'var(--ink2)' }}>
                  <span aria-hidden className="relative shrink-0" style={{ width: 40, height: 24, borderRadius: 12, background: erweitert ? 'var(--ok)' : 'var(--fill3)', transition: 'background .25s' }}>
                    <span className="sk-knauf absolute" style={{ top: 2, left: erweitert ? 18 : 2, width: 20, height: 20, borderRadius: '50%', background: 'var(--sk-av-ink)', boxShadow: '0 2px 5px rgba(0,0,0,0.25)' }} />
                  </span>
                  Erweiterte Kontaktinformationen einblenden?
                </button>
              )}
            </fieldset>
          );
        })}

        <div className="flex" style={{ gap: 10 }}>
          <button type="button" onClick={onSchliessen} className="sk-chip flex-1 cursor-pointer"
            style={{ height: 46, border: 'none', borderRadius: 23, fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>
            Abbrechen
          </button>
          <button type="button" onClick={() => void absenden()} disabled={saving} className="sk-akzent flex flex-1 cursor-pointer items-center justify-center disabled:opacity-50"
            style={{ height: 46, border: 'none', borderRadius: 23, gap: 8, fontSize: 14, fontWeight: 800, color: 'var(--acc-ink)', background: 'var(--acc-fill)', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.35), 0 12px 24px -12px var(--shade)' }}>
            {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk weight="bold" size={16} />}
            {bearbeiten ? 'Speichern' : 'Kontakt erstellen'}
          </button>
        </div>
      </div>
      {meldung && <MeldungDialog fehler text={meldung} onSchliessen={() => setMeldung(null)} />}
    </Sheet>
  );
}
